/**
 * Inventory of stylesheets and scripts already loaded in the document.
 *
 * The HTML-swap and partial-response paths both diff the target
 * page's asset list against this inventory to figure out which
 * CSS and JS URLs still need to be fetched. Everything already
 * on the page is skipped, which is what prevents double-loading
 * and the `const X already declared` errors that would otherwise
 * surface when a navigation lands on a page whose aggregated JS
 * bundle overlaps an already-evaluated bundle.
 *
 * The inventory is a pure set-semantics wrapper on top of URL
 * strings. The source of truth is the live DOM - the constructor
 * seeds it from existing `<link rel="stylesheet">` and
 * `<script src>` elements - and subsequent asset loads register
 * their URL via `markStylesheetLoaded` / `markScriptLoaded`.
 *
 * Stylesheets are keyed by full URL; scripts by origin + pathname
 * (see `scriptKey`) because re-executing a script is destructive
 * while re-applying a stylesheet is not.
 */

export class AssetInventory {
  private readonly stylesheets = new Set<string>();
  private readonly scripts = new Set<string>();

  constructor() {
    document
      .querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
      .forEach((el) => {
        if (el.href) this.stylesheets.add(el.href);
      });
    document
      .querySelectorAll<HTMLScriptElement>('script[src]')
      .forEach((el) => {
        if (el.src) this.scripts.add(this.scriptKey(el.src));
      });
  }

  /**
   * Key a script by origin + pathname, ignoring the query string.
   *
   * Drupal's aggregated JS is content-addressed in the filename
   * (`js_<hash>.js`); the query string only carries request metadata
   * (scope, delta, language, theme, include), so the same bundle is
   * served at one path under many queries. Keyed by full URL, a
   * navigation whose aggregate query differs (e.g. crossing
   * languages) re-injects and re-executes an already-evaluated
   * bundle - and re-running core/misc/drupal.js resets
   * `window.Drupal`, wiping every registered behavior including the
   * SDX mount runtime.
   */
  private scriptKey(src: string): string {
    try {
      const url = new URL(src, document.baseURI);
      return url.origin + url.pathname;
    } catch {
      return src;
    }
  }

  hasStylesheet(href: string): boolean {
    return this.stylesheets.has(href);
  }

  hasScript(src: string): boolean {
    return this.scripts.has(this.scriptKey(src));
  }

  markStylesheetLoaded(href: string): void {
    this.stylesheets.add(href);
  }

  markScriptLoaded(src: string): void {
    this.scripts.add(this.scriptKey(src));
  }
}
